/** The partner's claim links (newest first): who each went to, who sent it, when it expires,
 *  and the key a claimed one minted. "Send claim link" (console_partners_manage) is the
 *  hand-over and the rotation; a pending link can be revoked. Audited server-side
 *  (`partner.claim_link_sent` / `partner.claim_link_revoked`). */
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { ChipProps } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as partnersApi from '@/api/partners';
import type { PartnerClaimLink, PartnerClaimLinkStatus, PartnerDetail } from '@/api/partners';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { Section } from './PartnerSection';
import { SendClaimLinkDialog } from './SendClaimLinkDialog';
import { partnerErrorMessage } from './partnerErrors';

const STATUS_COLOR: Record<PartnerClaimLinkStatus, ChipProps['color']> = {
  pending: 'info',
  claimed: 'success',
  expired: 'default',
  revoked: 'default',
};

function ClaimLinkStatusChip({ status }: { status: PartnerClaimLinkStatus }) {
  return (
    <Chip
      size="small"
      variant={status === 'pending' || status === 'claimed' ? 'filled' : 'outlined'}
      color={STATUS_COLOR[status]}
      label={partnersApi.CLAIM_LINK_STATUS_LABELS[status] ?? status}
    />
  );
}

export const claimLinksKey = (partnerId: number) => ['partners', 'claim-links', partnerId] as const;

export function PartnerClaimLinksSection({ partner, canManage }: { partner: PartnerDetail; canManage: boolean }) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [sending, setSending] = useState(false);
  const [revoking, setRevoking] = useState<PartnerClaimLink | null>(null);

  const query = useQuery({
    queryKey: claimLinksKey(partner.id),
    queryFn: () => partnersApi.listClaimLinks(partner.id),
  });

  const revoke = useMutation({
    mutationFn: (link: PartnerClaimLink) => partnersApi.revokeClaimLink(partner.id, link.id),
    onSuccess: () => snackbar.show('Claim link revoked.', 'success'),
    // 409: it was claimed, expired or revoked meanwhile — say so, and show the list as it is.
    onError: (e) => snackbar.show(partnerErrorMessage(e, 'The claim link could not be revoked.'), 'error'),
    onSettled: () => {
      setRevoking(null);
      void qc.invalidateQueries({ queryKey: ['partners'] });
    },
  });

  const links = query.data ?? [];

  return (
    <Section
      title="Claim links"
      actions={
        canManage && (
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setSending(true)}>
            Send claim link
          </Button>
        )
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        A claim link is a one-time link, valid 72 hours, e-mailed to a technical contact. Confirming it
        mints the partner’s developer key and shows it to the contact once — ops never see it. Sending a
        new link is also how a key is rotated: a second key is allowed during rotation, and the older one
        keeps working for 7 more days once the new one is claimed.
      </Typography>

      {query.isLoading ? (
        <Skeleton variant="rounded" height={64} />
      ) : query.isError ? (
        <Alert severity="error">Couldn’t load the claim links.</Alert>
      ) : links.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No claim link sent yet.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Claim links">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Sent to</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Claimed</TableCell>
                {canManage && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {links.map((link) => (
                <TableRow key={link.id}>
                  <TableCell>
                    <ClaimLinkStatusChip status={link.status} />
                  </TableCell>
                  <TableCell>
                    {link.contact ? (
                      <>
                        {link.contact.name}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {link.contact.email}
                        </Typography>
                      </>
                    ) : (
                      <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        Contact removed
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <span title={formatDate(link.created_ts)}>{formatRelativeTime(link.created_ts)}</span>
                    {link.created_by && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        by {link.created_by.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(link.expires_ts)}</TableCell>
                  <TableCell>
                    {link.claimed_ts ? (
                      <>
                        {formatDate(link.claimed_ts)}
                        {link.credential && (
                          <Typography variant="caption" display="block">
                            Key{' '}
                            <RouterLink to={`/api-credentials/${link.credential.id}`} style={{ color: '#0a1a3f' }}>
                              {link.credential.name}
                            </RouterLink>
                            {link.credential.key_prefix && ` · ${link.credential.key_prefix}…`}
                          </Typography>
                        )}
                        {link.claimed_ip && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            from {link.claimed_ip}
                          </Typography>
                        )}
                      </>
                    ) : link.revoked_ts ? (
                      <Typography variant="caption" color="text.secondary">
                        Revoked {formatDate(link.revoked_ts)}
                      </Typography>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      {link.status === 'pending' && (
                        <Button
                          size="small"
                          color="error"
                          disabled={revoke.isPending}
                          onClick={() => setRevoking(link)}
                          aria-label={`Revoke the link sent to ${link.contact?.email ?? 'a removed contact'}`}
                        >
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {canManage && <SendClaimLinkDialog open={sending} partner={partner} onClose={() => setSending(false)} />}
      <ConfirmDialog
        open={revoking !== null}
        title="Revoke claim link"
        message={
          revoking
            ? `Revoke the link sent to ${revoking.contact?.email ?? 'a removed contact'}? It stops working at once; no key is minted. Send a new link when the partner is ready.`
            : ''
        }
        confirmLabel="Revoke"
        destructive
        onConfirm={() => revoking && revoke.mutate(revoking)}
        onCancel={() => setRevoking(null)}
      />
    </Section>
  );
}
